import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { notificationService, Notification } from '@/services/notificationService';
import { toast } from 'sonner';

function getNotificationIcon(type: string): string {
  switch (type) {
    case 'client': return '👤';
    case 'workout': return '💪';
    case 'report': return '📊';
    case 'message': return '💬';
    case 'success': return '✅';
    case 'warning': return '⚠️';
    case 'info': return 'ℹ️';
    case 'payment': return '💳';
    case 'report_received': return '📊';
    case 'workout_assigned': return '💪';
    case 'workout_completed': return '🏆';
    default: return '🔔';
  }
}

interface NotificationsContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  deleteAllNotifications: () => Promise<void>;
  createTestNotification: () => Promise<any>;
  refresh: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousNotificationIds = useRef<Set<string>>(new Set());

  const isReadNotification = (n: Notification) => Boolean(n.read ?? n.is_read);
  const computeUnreadCount = (items: Notification[]) =>
    items.reduce((count, item) => count + (isReadNotification(item) ? 0 : 1), 0);

  const mapActionUrl = (url?: string): string | undefined => {
    if (!url) return undefined;
    const planMatch = url.match(/^\/(?:training-plans|plans)\/(.+)$/);
    if (planMatch) return `/dashboard/workout-plan/${planMatch[1]}`;
    return url;
  };

  const transformNotification = (n: Notification): Notification => {
    const rawUrl = n.actionUrl || n.action_url || n.data?.actionUrl || n.data?.url;
    return {
      ...n,
      message: n.message || n.body,
      read: n.read ?? n.is_read,
      actionUrl: mapActionUrl(rawUrl),
      time: n.time || new Date(n.created_at),
    };
  };

  const fetchNotifications = useCallback(async () => {
    try {
      // Skip on public/unauthenticated pages (e.g. landing) to avoid noisy errors.
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      const { data } = await notificationService.getNotifications(1, 20);
      const transformed = (data || []).map(transformNotification);
      setNotifications(transformed);
      transformed.forEach(n => previousNotificationIds.current.add(n.id));
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const showNotificationToast = useCallback((notification: Notification) => {
    const transformed = transformNotification(notification);
    toast(transformed.title, {
      description: transformed.message || transformed.body,
      position: 'top-right',
      duration: 5000,
      icon: getNotificationIcon(transformed.type),
      action: transformed.actionUrl?.startsWith('/') ? {
        label: 'Zobacz',
        onClick: () => { window.location.href = transformed.actionUrl!; }
      } : undefined
    });
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Single Realtime subscription — cleanup properly handled via ref outside async
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      channel = supabase
        .channel('notifications:single')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
          (payload) => {
            if (!payload.new || previousNotificationIds.current.has(payload.new.id)) return;
            const newNotification = payload.new as Notification;
            const transformed = transformNotification(newNotification);
            showNotificationToast(newNotification);
            previousNotificationIds.current.add(newNotification.id);
            setNotifications(prev => {
              if (prev.some(item => item.id === transformed.id)) return prev;
              const next = [transformed, ...prev];
              setUnreadCount(computeUnreadCount(next));
              return next;
            });
          }
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
          (payload) => {
            if (!payload.new) { fetchNotifications(); return; }
            const updated = transformNotification(payload.new as Notification);
            setNotifications(prev => {
              const index = prev.findIndex(item => item.id === updated.id);
              if (index === -1) return prev;
              const next = [...prev];
              next[index] = { ...next[index], ...updated };
              setUnreadCount(computeUnreadCount(next));
              return next;
            });
          }
        )
        .on(
          'postgres_changes',
          { event: 'DELETE', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
          (payload) => {
            const deletedId = payload.old?.id as string | undefined;
            if (!deletedId) { fetchNotifications(); return; }
            setNotifications(prev => {
              const next = prev.filter(item => item.id !== deletedId);
              setUnreadCount(computeUnreadCount(next));
              return next;
            });
            previousNotificationIds.current.delete(deletedId);
          }
        )
        .subscribe();
    };

    setupSubscription();

    // Cleanup runs synchronously when the effect is torn down
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
        channel = null;
      }
    };
  }, [fetchNotifications, showNotificationToast]);

  const markAsRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await notificationService.markAsRead(id);
    } catch {
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
    setUnreadCount(0);
    try {
      await notificationService.markAllAsRead();
    } catch {
      fetchNotifications();
    }
  };

  const deleteNotification = async (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    try {
      await notificationService.deleteNotification(id);
    } catch {
      fetchNotifications();
    }
  };

  const deleteAllNotifications = async () => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      await notificationService.deleteAllNotifications();
    } catch {
      fetchNotifications();
    }
  };

  return (
    <NotificationsContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllNotifications,
      createTestNotification: notificationService.createTestNotification,
      refresh: fetchNotifications,
    }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotificationsContext must be used inside NotificationsProvider');
  return ctx;
}
